import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  minHeight?: number;
}

const TOOLBAR_BTN: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  width: 28, height: 26, padding: 0,
  border: '1px solid transparent', borderRadius: 4,
  background: 'none', cursor: 'pointer',
  fontSize: '0.8125rem', fontWeight: 700, color: 'var(--color-text-muted)',
  transition: 'background 0.1s, color 0.1s',
};

const TOOLBAR_BTN_ACTIVE: React.CSSProperties = {
  ...TOOLBAR_BTN,
  background: 'var(--color-primary)',
  color: '#fff',
  border: '1px solid var(--color-primary)',
};

const DIVIDER: React.CSSProperties = {
  width: 1, height: 18, background: 'var(--color-border)', margin: '0 4px',
  alignSelf: 'center', flexShrink: 0,
};

function TBtn({
  active, onClick, title, children,
}: { active?: boolean; onClick: () => void; title: string; children: React.ReactNode }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      style={active ? TOOLBAR_BTN_ACTIVE : TOOLBAR_BTN}
      onMouseEnter={(e) => {
        if (!active) {
          (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-bg)';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text)';
        }
      }}
      onMouseLeave={(e) => {
        if (!active) {
          (e.currentTarget as HTMLButtonElement).style.background = 'none';
          (e.currentTarget as HTMLButtonElement).style.color = 'var(--color-text-muted)';
        }
      }}
    >
      {children}
    </button>
  );
}

export function RichTextEditor({ value, onChange, placeholder, minHeight = 200 }: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [StarterKit],
    content: value,
    onUpdate: ({ editor }) => {
      const html = editor.isEmpty ? '' : editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'rte-body',
        style: `min-height:${minHeight}px;outline:none;`,
        ...(placeholder ? { 'data-placeholder': placeholder } : {}),
      },
    },
  });

  // Sync external reset (value → '' clears editor)
  useEffect(() => {
    if (!editor) return;
    if (value === '' && !editor.isEmpty) {
      editor.commands.clearContent(true);
    }
  }, [value, editor]);

  if (!editor) return null;

  const e = editor;

  return (
    <div style={{
      border: '1px solid var(--color-border)',
      borderRadius: 'var(--radius-sm)',
      overflow: 'hidden',
      background: 'var(--color-bg-card)',
    }}>
      {/* Toolbar */}
      <div style={{
        display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2,
        padding: '0.3125rem 0.5rem',
        borderBottom: '1px solid var(--color-border)',
        background: '#f8fafc',
      }}>
        <TBtn active={e.isActive('bold')} onClick={() => e.chain().focus().toggleBold().run()} title="Fett (Strg+B)">
          <strong>B</strong>
        </TBtn>
        <TBtn active={e.isActive('italic')} onClick={() => e.chain().focus().toggleItalic().run()} title="Kursiv (Strg+I)">
          <em style={{ fontStyle: 'italic' }}>I</em>
        </TBtn>
        <TBtn active={e.isActive('strike')} onClick={() => e.chain().focus().toggleStrike().run()} title="Durchgestrichen">
          <span style={{ textDecoration: 'line-through' }}>S</span>
        </TBtn>
        <div style={DIVIDER} />
        <TBtn active={e.isActive('heading', { level: 3 })} onClick={() => e.chain().focus().toggleHeading({ level: 3 }).run()} title="Überschrift">
          H3
        </TBtn>
        <div style={DIVIDER} />
        <TBtn active={e.isActive('bulletList')} onClick={() => e.chain().focus().toggleBulletList().run()} title="Aufzählung">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <circle cx="4" cy="6" r="2"/><rect x="8" y="5" width="13" height="2" rx="1"/>
            <circle cx="4" cy="12" r="2"/><rect x="8" y="11" width="13" height="2" rx="1"/>
            <circle cx="4" cy="18" r="2"/><rect x="8" y="17" width="13" height="2" rx="1"/>
          </svg>
        </TBtn>
        <TBtn active={e.isActive('orderedList')} onClick={() => e.chain().focus().toggleOrderedList().run()} title="Nummerierte Liste">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <text x="1" y="7" fontSize="7" fontWeight="bold">1.</text>
            <rect x="8" y="5" width="13" height="2" rx="1"/>
            <text x="1" y="13" fontSize="7" fontWeight="bold">2.</text>
            <rect x="8" y="11" width="13" height="2" rx="1"/>
            <text x="1" y="19" fontSize="7" fontWeight="bold">3.</text>
            <rect x="8" y="17" width="13" height="2" rx="1"/>
          </svg>
        </TBtn>
        <div style={DIVIDER} />
        <TBtn active={e.isActive('blockquote')} onClick={() => e.chain().focus().toggleBlockquote().run()} title="Zitat">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V20c0 1 0 1 1 1zm12 0c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3c0 1 0 1 1 1z"/>
          </svg>
        </TBtn>
      </div>

      {/* Editor body */}
      <style>{`
        .rte-body { padding: 0.625rem 0.75rem; color: var(--color-text); font-size: 0.9375rem; line-height: 1.6; }
        .rte-body:empty::before { content: attr(data-placeholder); color: var(--color-text-muted); pointer-events: none; }
        .rte-body p { margin: 0 0 0.5em; }
        .rte-body p:last-child { margin-bottom: 0; }
        .rte-body h3 { font-size: 1rem; font-weight: 700; margin: 0.75em 0 0.25em; }
        .rte-body ul, .rte-body ol { padding-left: 1.5em; margin: 0.25em 0; }
        .rte-body li { margin: 0.125em 0; }
        .rte-body blockquote { border-left: 3px solid var(--color-border); margin: 0.5em 0; padding-left: 0.75em; color: var(--color-text-muted); }
        .rte-body strong { font-weight: 700; }
        .rte-body em { font-style: italic; }
        .rte-body s { text-decoration: line-through; }
      `}</style>
      <div
        style={{ cursor: 'text' }}
        onClick={() => editor.commands.focus()}
      >
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}

/** Rendert gespeichertes HTML sicher im Activity-Feed */
export function RichTextDisplay({ html, className, style }: { html: string; className?: string; style?: React.CSSProperties }) {
  const isHtml = html.trimStart().startsWith('<');
  if (!isHtml) {
    return <div className={className} style={{ whiteSpace: 'pre-wrap', ...style }}>{html}</div>;
  }
  return (
    <>
      <style>{`
        .rte-display p { margin: 0 0 0.4em; } .rte-display p:last-child { margin-bottom: 0; }
        .rte-display h3 { font-size: 0.9375rem; font-weight: 700; margin: 0.5em 0 0.2em; }
        .rte-display ul, .rte-display ol { padding-left: 1.25em; margin: 0.2em 0; }
        .rte-display li { margin: 0.1em 0; }
        .rte-display blockquote { border-left: 3px solid var(--color-border); margin: 0.3em 0; padding-left: 0.625em; opacity: 0.75; }
        .rte-display strong { font-weight: 700; } .rte-display em { font-style: italic; }
        .rte-display s { text-decoration: line-through; }
      `}</style>
      <div
        className={`rte-display${className ? ' ' + className : ''}`}
        style={style}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </>
  );
}
